// @flow
import * as React from 'react';
import L from 'leaflet';
import {
    Asset,
    Cable,
    Fiber,
    GisLine,
    GisPoint,
    Region,
    Pipe,
    Point11,
    Point12,
    Rack,
    Splice,
} from 'esm-web-pro/containers/pages/network/objects';
import PointIconResource from 'esm-web-pro/api/resources/PointIconResource';
import OpticalPort from 'esm-web-pro/containers/pages/network/objects/opticalport/OpticalPort';
import DataPort from 'esm-web-pro/containers/pages/network/objects/dataport/DataPort';
import proj4 from 'proj4';
import SFP from 'esm-web-pro/containers/pages/network/objects/sfp/SFP';
import ContinuousPipe from 'esm-web-pro/containers/pages/network/objects/continuous-pipe/ContinuousPipe';
import { isEmpty, sortBy } from 'lodash';
import type { HexColor, ID } from 'esm-web-pro/types/common';
import { POINTS_WITH_CABLE_RESERVE_DISPLAY } from 'esm-web-pro/redux/networks/constants';

const iconIncrementSize = 0;
const iconWidth = 27;
const iconHeight = 42;

const defaultIconOptions: Object = {
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [iconWidth / 2, iconHeight],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [iconHeight, iconHeight],
};

const getIconName = (point: string = '', undergroundConnection: boolean = false): string => {
    if (point && point !== 'gispoint') {
        if (point === 'wallpoint' || point === 'cabinet' || point === 'pillar') {
            if (undergroundConnection) {
                return `${point}_2`;
            }
        }
        return point;
    }
    return 'marker_questionmark';
};

const pointIcon = (
    point: string,
    disabled: boolean = false,
    providerColor: string = '#bdcc32',
    undergroundConnection: boolean = false,
    contentCount: ?number = 0,
    cableReserves: ?number = undefined,
) => {
    const iconName = getIconName(point, undergroundConnection);
    const color = disabled ? '#979FB2' : providerColor;
    let contentText = contentCount;

    const iconUrl = PointIconResource.getPointIconUrl(iconName, color);
    if ((contentCount > 0) || (cableReserves !== undefined && cableReserves > 0)) {
        const divIcon = L.divIcon({
            ...defaultIconOptions,
            html: L.icon({ iconUrl }).createIcon(),
        });

        divIcon.initialize({
            className: 'networks-numbered-point-icon',
            iconSize: [27, 42],
            iconUrl,
        });
        const icon = divIcon.createIcon();

        const pointText = icon.getElementsByClassName('networks-content-text')[0];
        if (cableReserves !== undefined) {
            contentText = `${contentCount}/${cableReserves}`;
        }

        if (pointText) {
            icon.getElementsByClassName('networks-content-text')[0].innerHTML = contentText.toString();
        } else {
            const countEl = document.createElement('h2');
            countEl.className = 'networks-content-text';
            countEl.innerText = contentText.toString();
            icon.appendChild(countEl);
        }

        return new L.DivIcon({
            className: 'networks-point-div-icon',
            html: icon,
            iconAnchor: [iconWidth / 2, iconHeight],
            iconSize: [iconWidth, iconHeight],
        });
    }
    return L.icon({
        ...defaultIconOptions,
        iconUrl,
        className: 'networks-point-icon',
    });
};

const canConnect = (p1: Object, p2: Object) => {
    if (!p1 || !p2) {
        return false;
    }

    return (p1.child.underground_connection && p2.child.underground_connection)
        || (p1.child.overground_connection && p2.child.overground_connection);
};

const OBJECT_COMPONENTS = {
    gisline: GisLine,
    pipe: Pipe,
    cable: Cable,
    gispoint: GisPoint,
    shaft: Point11,
    pillar: Point11,
    cabinet: Point11,
    wallpoint: Point11,
    pop: Point11,
    linecut: Point11,
    fork: Point11,
    coupler: Point12,
    odf: Point12,
    switch: Point12,
    terminalbox: Point12,
    fiber: Fiber,
    splice: Splice,
    opticalodfport: OpticalPort,
    opticalswitchport: OpticalPort,
    dataport: DataPort,
    rack: Rack,
    sfp: SFP,
    continuousPipe: ContinuousPipe,
    region: Region,
    asset: Asset,
};

const RESOURCES = {
    gisline: 'gisLine',
    pipe: 'pipe.geo',
    cable: 'cable.geo',
    gispoint: 'gisPoint',
    shaft: 'shaft',
    pillar: 'pillar',
    cabinet: 'cabinet',
    wallpoint: 'wallpoint',
    pop: 'pop',
    asset: 'asset',
    linecut: 'linecut',
    fork: 'fork',
    coupler: 'coupler',
    odf: 'odf',
    switch: 'switch',
    terminalbox: 'terminalbox',
    fiber: 'fiber.geo',
    splice: 'splice',
    rack: 'rack',
    patch_cable: 'patchCable',
    opticalodfport: 'opticalodfport',
    opticalswitchport: 'opticalswitchport',
    dataport: 'dataport',
    sfp: 'sfp',
    continuousPipe: 'continuousPipe',
    gisregion: 'gisRegions.geo',
};

const MAP_RESOURCES = {
    ...RESOURCES,
    gispoint: 'gisPoint.geo',
    gisline: 'gisLine.geo',

    shaft: 'shaft.geo',
    pillar: 'pillar.geo',
    cabinet: 'cabinet.geo',
    wallpoint: 'wallpoint.geo',
    pop: 'pop.geo',
    rack: 'rack.geo',
    linecut: 'linecut.geo',
    fork: 'fork.geo',
    continuousPipe: 'continuousPipe.geo',

    coupler: 'coupler.geo',
    odf: 'odf.geo',
    switch: 'switch.geo',
    terminalbox: 'terminalbox.geo',

    splice: 'splice.geo',
    opticalodfport: 'opticalodfport.geo',
    opticalswitchport: 'opticalswitchport.geo',
    dataport: 'dataport.geo',
    sfp: 'sfp.geo',
    region: 'gisRegions.geo',
};

const FIELDS_SET_FROM_MODEL = {
    odf: { heightU: 'heightU', optical_port_count: 'port_count', export: 'eit_export_default' },
    switch: { heightU: 'heightU', export: 'eit_export_default' },
    cable: { fiber_count: 'fibers_count', tube_count: 'tube_count', export: 'eit_export_default' },
    patch_cable: { fiber_count: 'fibers_count', tube_count: 'tube_count', export: 'eit_export_default' },
    shaft: { export: 'eit_export_default', has_sot: 'has_sot_default' },
    pop: { export: 'eit_export_default' },
    pillar: { export: 'eit_export_default' },
    terminalbox: { export: 'eit_export_default' },
    coupler: { export: 'eit_export_default' },
    pipe: { export: 'eit_export_default' },
    fiber: { attenuation: 'attenuation' },
};

const MODEL_FIELD_NAME = {
    patch_cable: 'model',
    cable: 'model',
    odf: 'model',
    switch: 'model',
    shaft: 'model',
    pillar: 'model',
    terminalbox: 'model',
    coupler: 'model',
    pipe: 'type',
    fiber: 'model',
    sfp: 'model',
    pop: 'model',
};

const numberedPointIcon = (
    layer: Object,
    number: number,
    focused: boolean = false,
    objectType: string = '',
    providerColor: string = '#bdcc32',
    undergroundConnection: boolean = false,
) => {
    const count = number;

    const feature = layer.feature.properties;

    const iconName = getIconName(feature ? feature.point?.child?.type : objectType,
        feature ? feature?.point?.child?.underground_connection : undergroundConnection);
    const color = feature && feature.point ? feature.point.color : providerColor;

    const iconUrl = PointIconResource.getPointIconUrl(iconName, color, true);

    const previousIcon = L.divIcon({
        ...defaultIconOptions,
        html: L.icon({ iconUrl }).createIcon(),
    });
    previousIcon.initialize({
        className: 'networks-numbered-point-icon',
        iconSize: [31, 48],
        iconUrl,
    });

    const icon = previousIcon.createIcon();

    const pointText = icon.getElementsByClassName('networks-point-text')[0];
    if (pointText) {
        icon.getElementsByClassName('networks-point-text')[0].innerHTML = count;
    } else {
        const countEl = document.createElement('h2');
        countEl.className = 'networks-point-text';
        countEl.innerText = count.toString();
        icon.appendChild(countEl);
    }
    // add the same logic for reserved cables here
    if (feature && feature.point && (feature.point.content_count > 0 || feature.point.cable_reserves > 0)) {
        let featureCount = feature.point.content_count;
        const featureCableReserves = feature.point.cable_reserves;

        if (POINTS_WITH_CABLE_RESERVE_DISPLAY.includes(feature.point.child?.type)) {
            featureCount = `${featureCount}/${featureCableReserves}`;
        }

        const contentText = icon.getElementsByClassName('networks-content-text-focused')[0];
        if (contentText) {
            icon.getElementsByClassName('networks-content-text-focused')[0].innerHTML = featureCount.toString();
        } else {
            const countEl = document.createElement('h2');
            countEl.className = 'networks-content-text-focused';
            countEl.innerText = featureCount.toString();
            icon.appendChild(countEl);
        }
    }

    return new L.DivIcon({
        className: 'networks-point-container',
        html: icon,
        iconAnchor: focused ? [iconWidth / 1.8, iconHeight + 5] : [iconWidth / 1.8, iconHeight + 7],
        iconSize: [iconWidth + (focused ? iconIncrementSize : 0), iconHeight + (focused ? iconIncrementSize : 0)],
    });
};

const cleanUpNumberedPointIcon = (layer: Object) => {
    const feature = layer.feature.properties;

    return pointIcon(feature.point?.child?.type, false, feature.point ? feature.point.color : '#bdcc32',
        feature.point?.child?.underground_connection, feature.point && feature.point.content_count);
};

proj4.defs([
    [
        process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM,
        process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM_DEF,
    ],
]);

const proj4Instance = proj4(process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM);

const transformPointCoords = (coords: [number, number]) => proj4Instance.forward(coords);
const reverseTransformPointCoords = (coords: [number, number]) => proj4Instance.inverse(coords);

const getPointIcon = (point, disabled = false) => (point ? pointIcon(point.child.type, disabled, point.color,
    point.child?.underground_connection, point.content_count)
    : pointIcon(point, disabled));

const createLineCallback = (points, checkIfPointIsSelectable, onClick) => {
    const disabledPoints = [];
    const pointsWithClickHandlers = [];

    return {
        run: (layer) => {
            // TODO: Do not exclude address points but filter only networks points by class name or other property
            const layerIsPoint = layer.feature && layer.feature.geometry.type === 'Point'
                && layer.options.icon.options.className !== 'address-custom-number';

            if (!layerIsPoint || (points && points.find((p) => p.layer === layer))) {
                return;
            }

            const lastPointLayer = points ? points[points.length - 1]?.layer : null;
            const newPoint = layer.feature.properties.point;
            const canClick = checkIfPointIsSelectable(newPoint, lastPointLayer, points?.length);

            if (canClick) {
                layer.on('click', onClick);
                const enabledIcon = getPointIcon(newPoint);
                layer.setIcon(enabledIcon);
                pointsWithClickHandlers.push(layer);
            } else {
                layer.off('click', onClick);
                const disabledIcon = getPointIcon(newPoint, true);
                layer.setIcon(disabledIcon);
                disabledPoints.push(layer);
            }
        },
        cleanUp: () => {
            disabledPoints.forEach((layer) => {
                const { feature: { properties: { point } } } = layer;
                const enabledIcon = getPointIcon(point);
                layer.setIcon(enabledIcon);
            });

            pointsWithClickHandlers.forEach((layer) => {
                layer.off('click', onClick);
            });
        },
    };
};

const getLineBetweenTwoPoints = (map, pointCoordinates, lastPointCoordinates) => {
    const startPointLines = map.coordsToLayers[pointCoordinates.join('-')].lines;
    const endPointLines = map.coordsToLayers[lastPointCoordinates.join('-')]
        && map.coordsToLayers[lastPointCoordinates.join('-')].lines;

    let currLineLayer = null;
    if (!isEmpty(startPointLines) && !isEmpty(endPointLines)) {
        currLineLayer = startPointLines.find((line) => {
            const firstLineCoordinate = JSON.stringify(line.feature.geometry.coordinates[0]);
            const lastLineCoordinate = JSON.stringify(line.feature.geometry.coordinates
                // eslint-disable-next-line no-unexpected-multiline
                [line.feature.geometry.coordinates.length - 1]);
            const lastPointLayerCoordinate = JSON.stringify(lastPointCoordinates);
            return (firstLineCoordinate === lastPointLayerCoordinate)
                || (lastLineCoordinate === lastPointLayerCoordinate);
        });
    }
    return currLineLayer;
};

const DEFAULT_PROVIDER_COLOR = '#bdcc32';


const getPointColor = (pointLayer: Object) => {
    const {
        point: pointLayerPoint,
    } = pointLayer.feature.properties;

    return pointLayerPoint ? pointLayerPoint.color : DEFAULT_PROVIDER_COLOR;
};

const getPointProvider = (pointLayer: Object) => {
    const {
        point: pointLayerPoint,
    } = pointLayer.feature.properties;

    return pointLayerPoint.infrastructureProvider;
};


export const UNDEFINED_PROVIDER_COLOR = '#868484';

const determineProviderColor = (lines: Array<{
    type: 'airroute' | 'pipe',
    infrastructure_provider: ID,
    diameter: ?number,
    color: HexColor
}>) => {
    const providers = new Set(lines.map((line) => line.infrastructure_provider));
    if (providers.size === 1) {
        return lines[0].color;
    }

    const airRoute = lines.find((line) => line.type === 'airroute');
    if (airRoute) {
        if (lines.find((line) => line.infrastructure_provider !== airRoute.infrastructure_provider)) {
            return UNDEFINED_PROVIDER_COLOR;
        }

        return airRoute.color;
    }

    const pipes = sortBy(lines, ['diameter']);
    const maxDiameterPipe = pipes.pop();

    if (pipes.find((pipe) => pipe.diameter === maxDiameterPipe.diameter
        && pipe.infrastructure_provider !== maxDiameterPipe.infrastructure_provider)) {
        return UNDEFINED_PROVIDER_COLOR;
    }

    return maxDiameterPipe.color;
};

const isPointSnappedToLine = (map, pointLatLng, lineLatLngs: Array<[number, number]>) => {
    const toPoint = (latLng) => map.latLngToLayerPoint(latLng);

    return lineLatLngs.some((latLng, i) => {
        if (i === 0) return false;
        const distance = L.LineUtil.pointToSegmentDistance(
            toPoint(pointLatLng),
            toPoint(latLng),
            toPoint(lineLatLngs[i - 1]),
        );
        return distance < 1;
    });
};

const getSegmentBetweenPoints = (map, point1LatLng, point2LatLng, lineLatLngs: Array<[number, number]>) => {
    const toPoint = (latLng) => map.latLngToLayerPoint(latLng);

    const getClosestSegment = (pointLatLng) => {
        let smallestDistance = Number.MAX_VALUE;
        let segmentIndex = -1;

        lineLatLngs.forEach((latLng, i) => {
            if (i > 0) {
                const distance = L.LineUtil.pointToSegmentDistance(
                    toPoint(pointLatLng),
                    toPoint(latLng),
                    toPoint(lineLatLngs[i - 1]),
                );
                if (distance < smallestDistance) {
                    smallestDistance = distance;
                    segmentIndex = i;
                }
            }
        });

        return segmentIndex;
    };

    if (lineLatLngs.length === 2) {
        return [point1LatLng, point2LatLng];
    }

    const segment1Index = getClosestSegment(point1LatLng);
    const segment2Index = getClosestSegment(point2LatLng);

    if (segment1Index === segment2Index) {
        return [point1LatLng, point2LatLng];
    }

    if (segment1Index > segment2Index) {
        return [point2LatLng, ...lineLatLngs.slice(segment2Index, segment1Index).reverse(), point1LatLng];
    }

    return [point1LatLng, ...lineLatLngs.slice(segment1Index, segment2Index), point2LatLng];
};


export {
    canConnect, pointIcon, OBJECT_COMPONENTS, RESOURCES, MAP_RESOURCES, numberedPointIcon,
    FIELDS_SET_FROM_MODEL, MODEL_FIELD_NAME, cleanUpNumberedPointIcon, transformPointCoords,
    reverseTransformPointCoords, createLineCallback, getLineBetweenTwoPoints,
    getPointColor, getPointProvider, determineProviderColor,
    isPointSnappedToLine, getSegmentBetweenPoints,
};

export const LAT_LNG_MAX_MARGIN = 0.000001;
export const COORDINATES_EDIT_ALLOWED_DIGITS = 6;
